// Persistent model collection, backed by IndexedDB (handles the thumbnail data
// URLs without localStorage's ~5MB cap). Plus JSON file export/import so the
// library can be backed up or moved between browsers.

import { type SavedModel, isSavedModel } from "./saved-model";
import { downloadJson, newId, safeFileName } from "./idb-store";
import { deleteBake } from "./bake-store";
import { createSyncedStore } from "../sync/synced-store";
import { syncController } from "../sync/controller";
import { createScopedStore } from "../sync/account-scope";

// Local IndexedDB, scoped per signed-in account (the legacy database is the
// anonymous pool) and mirrored to the server once a user signs in (a no-op
// passthrough until then). Reads stay local; writes queue for sync.
const store = createSyncedStore<SavedModel>(
  "models",
  createScopedStore<SavedModel>("tinydraft", "models"),
  syncController,
  // The reference photo is a device-local aid (never uploaded) — preserve it
  // when a pull overwrites the model.
  { localOnlyFields: ["reference"] },
);

export function listModels(): Promise<SavedModel[]> {
  return store.list();
}

// Ids only — cheap (no record deserialize). For the one-time inline-bake
// migration, which then loads models one at a time.
export function listModelIds(): Promise<string[]> {
  return store.listKeys();
}

export function putModel(m: SavedModel): Promise<IDBValidKey> {
  return store.put(m);
}

export function getModel(id: string): Promise<SavedModel | undefined> {
  return store.get(id);
}

export async function deleteModel(id: string): Promise<undefined> {
  await store.remove(id);
  await deleteBake(id).catch(() => {}); // cascade-drop the (regenerable) bake
  return undefined;
}

export { newId };

// ── File export / import ─────────────────────────────────────────────────────

export function downloadModel(m: SavedModel): void {
  // Strip the reference image from exports — it's a browser-only aid and would
  // bloat the file with the embedded picture bytes.
  const exported: SavedModel & { baked?: unknown } = { ...m };
  delete exported.reference;
  // Strip any legacy inline baked asset (newer records keep it in the separate
  // bake store): derived typed-array data JSON would mangle + bloat the file.
  delete exported.baked;
  downloadJson(exported, `${safeFileName(m.name, "model")}.tinydraft.json`);
}

// Parse a picked file into a model; rejects malformed files. Keeps the file's
// ORIGINAL id (rather than minting a fresh one) so a model keeps its identity
// across devices/browsers — layouts reference models by id, so a transferred
// model must keep its id for the layout's placements to find it. Re-importing
// the same model therefore updates the existing entry instead of duplicating it.
// A malformed/missing id (shouldn't happen — isSavedModel requires one) gets a
// fresh id as a fallback.
export async function readModelFile(file: File): Promise<SavedModel> {
  const parsed: unknown = JSON.parse(await file.text());
  if (!isSavedModel(parsed)) throw new Error("Not a TinyDraft model file.");
  return { ...parsed, id: parsed.id || newId() };
}
